import { ApiError } from "@circle-react/config/CustomErrors";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import type { ReviewPromptAfterDays } from "@circle-react/lib/queries/reviewSettings";
import {
  reviewSettingsQueries,
  useUpdateReviewSettingsMutation,
} from "@circle-react/lib/queries/reviewSettings";
import { settingsRoutes } from "@circle-react/lib/routes/settings";
import { Box } from "@circleco/compass/components/Box";
import { Link } from "@circleco/compass/components/Link";
import { Select } from "@circleco/compass/components/Select";
import { Switch } from "@circleco/compass/components/Switch";
import { useToast } from "@circleco/compass/context/CompassContext";
import { Link as RouterLink } from "@tanstack/react-router";
import { useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { useScrollIntoViewByAnchor } from "@/react/hooks/utils/useScrollIntoViewByAnchor";

import { SettingsBlock } from "../SettingsBlock";

// Lets other pages deep-link here, e.g. `{ to: settingsRoutes.general(), hash: REVIEWS_SECTION_ANCHOR }`.
export const REVIEWS_SECTION_ANCHOR = "reviews";

// Saves immediately on change rather than through General's shared submit,
// so the controls are plain controlled Compass components -- not
// `Form.ToggleSwitch`/`Form.SelectNative`, which register as uncontrolled
// RHF fields and would fight an externally-controlled value.
const i18nRoot = "settings.reviews.manage_drawer";
const generalI18nRoot = "settings.general.reviews";

// Splits the translated string on the `%{here_link}` placeholder so the
// link renders as a real `<Link>` instead of literal text.
const HERE_LINK_TOKEN = "__HERE_LINK__";

const splitAroundHereLink = (translated: string): [string, string] => {
  const [before = "", after = ""] = translated.split(HERE_LINK_TOKEN);
  return [before, after];
};

const PROMPT_AFTER_DAYS_OPTIONS: ReviewPromptAfterDays[] = [7, 14, 30, 60, 90];

interface PromptAfterDaysOption {
  label: string;
  value: ReviewPromptAfterDays;
}

const getPromptAfterDaysOptions = (): PromptAfterDaysOption[] =>
  PROMPT_AFTER_DAYS_OPTIONS.map(value => ({
    value,
    label: t([i18nRoot, "prompt_after_days", "options", String(value)]),
  }));

export const ReviewsSettings = () => {
  const toast = useToast();
  const { currentCommunitySettings } = usePunditUserContext();
  const isReviewsFeatureEnabled = Boolean(
    currentCommunitySettings?.community_reviews_enabled,
  );
  const { data, isPending: isLoading } = useQuery({
    ...reviewSettingsQueries.show(),
    enabled: isReviewsFeatureEnabled,
  });
  const mutation = useUpdateReviewSettingsMutation();
  const scrollRef = useScrollIntoViewByAnchor(REVIEWS_SECTION_ANCHOR);

  if (!isReviewsFeatureEnabled || isLoading || !data) {
    return null;
  }

  const handleError = (error: unknown) => {
    const message = error instanceof ApiError ? error.message : undefined;
    toast.add({
      title: message ?? t([i18nRoot, "toast", "error"]),
      variant: "destructive",
      icon: "triangle-exclamation-filled",
      iconColor: "danger",
    });
  };

  const handleSuccess = () =>
    toast.add({
      title: t([i18nRoot, "toast", "success"]),
      variant: "primary",
      icon: "circle-check-filled",
      iconColor: "success",
    });

  const handleCollectReviewsChange = (checked: boolean) => {
    mutation.mutate(
      { collect_reviews_enabled: checked },
      { onSuccess: handleSuccess, onError: handleError },
    );
  };

  const handlePromptAfterDaysChange = (
    option: PromptAfterDaysOption | null,
  ) => {
    if (!option) {
      return;
    }
    mutation.mutate(
      { prompt_after_days: option.value },
      { onSuccess: handleSuccess, onError: handleError },
    );
  };

  const promptAfterDaysOptions = getPromptAfterDaysOptions();
  const selectedPromptAfterDays =
    promptAfterDaysOptions.find(
      option => option.value === data.prompt_after_days,
    ) ?? null;

  const [toggleDescriptionBeforeLink, toggleDescriptionAfterLink] =
    splitAroundHereLink(
      t([generalI18nRoot, "toggle_description"], {
        here_link: HERE_LINK_TOKEN,
      }),
    );

  return (
    <Box ref={scrollRef} className="scroll-mt-14">
      <SettingsBlock title={t("section_reviews")}>
        <Switch
          label={t([i18nRoot, "toggle_label"])}
          labelPosition="start"
          description={
            <>
              {toggleDescriptionBeforeLink}
              <Link
                renderLink={<RouterLink to={settingsRoutes.reviews()} />}
                color="link"
                size="sm"
              >
                {t([generalI18nRoot, "here_link"])}
              </Link>
              {toggleDescriptionAfterLink}
            </>
          }
          checked={data.collect_reviews_enabled}
          disabled={mutation.isPending}
          onCheckedChange={handleCollectReviewsChange}
        />

        <Select
          label={t([i18nRoot, "prompt_after_days", "label"])}
          disabled={mutation.isPending || !data.collect_reviews_enabled}
          options={promptAfterDaysOptions}
          value={selectedPromptAfterDays}
          onValueChange={handlePromptAfterDaysChange}
        />
      </SettingsBlock>
    </Box>
  );
};
